import React from 'react';
import { FormItem, SearchBox, SwitchAndInput } from 'components/elements';
import { Input, Select, Form, InputNumber, Switch } from 'antd';
import { EventTypeEnum, VirtualMeetingTypeEnum } from 'graphql/types';
import styled from 'styled-components';
import { colors } from 'settings/style';
import { isVirtualEventType, virtualMeetingTypeOptions } from './virtualMeetingTypes';

function EventDetailsStep() {
  return (
    <>
      <FormItem label="Enter Price" theme="primary">
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.price !== currentValues.price}
        >
          {({ getFieldValue, setFieldsValue }) => {
            const price = getFieldValue('price');
            return (
              <SwitchAndInput
                compact
                fieldValue={price}
                setFieldsValue={setFieldsValue}
                fieldName="price"
                switchOffDefaultValue={0}
                switchOnDefaultValue={15}
                checkedName="Free"
                unCheckedName="Premium"
              >
                <FormItem name="price" theme="primary" noStyle initialValue={0}>
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={(value) =>
                      `${value}`
                        .replace(/([^0-9.])/g, '')
                        .replace(/^(\d+.?\d{0,2})\d*$/, '$1')
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={(value) => value.replace(/([^0-9.])/g, '') as any}
                  />
                </FormItem>
              </SwitchAndInput>
            );
          }}
        </Form.Item>
      </FormItem>

      <FormItem label="Enter Capacity" theme="primary">
        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => prevValues.capacity !== currentValues.capacity}
        >
          {({ getFieldValue, setFieldsValue }) => {
            const capacity = getFieldValue('capacity');
            return (
              <SwitchAndInput
                compact
                fieldValue={capacity}
                setFieldsValue={setFieldsValue}
                fieldName="capacity"
                switchOffDefaultValue={0}
                switchOnDefaultValue={50}
                checkedName="Unlimited Seats"
                unCheckedName="Limited Seats"
              >
                <FormItem name="capacity" theme="primary" noStyle initialValue={0}>
                  <InputNumber
                    style={{ width: '100%' }}
                    formatter={(value) => `${value}`.replace(/([^0-9.])/g, '')}
                    parser={(value) => value.replace(/([^0-9.])/g, '') as any}
                  />
                </FormItem>
              </SwitchAndInput>
            );
          }}
        </Form.Item>
      </FormItem>

      <FormItem
        name="eventType"
        label="What is your Event Type"
        theme="primary"
        rules={[
          {
            required: true,
            message: 'Please Select your Event Type!',
          },
        ]}
      >
        <Select
          options={[
            { label: 'Physical Event', value: EventTypeEnum.Physical },
            { label: 'Virtual Event', value: EventTypeEnum.Virtual },
            { label: 'Both', value: EventTypeEnum.Both },
          ]}
        />
      </FormItem>
      <Form.Item
        noStyle
        shouldUpdate={(prevValues, currentValues) =>
          prevValues.eventType !== currentValues.eventType ||
          prevValues.virtualMeetingType !== currentValues.virtualMeetingType
        }
      >
        {({ getFieldValue }) => {
          const results = [];
          const eventType = getFieldValue('eventType');
          const virtualMeetingType = getFieldValue('virtualMeetingType') ?? VirtualMeetingTypeEnum.AutoCreateZoom;

          if (isVirtualEventType(eventType)) {
            results.push(
              <FormItem
                key="virtualMeetingType"
                name="virtualMeetingType"
                label="Online meeting setup"
                theme="primary"
                initialValue={VirtualMeetingTypeEnum.AutoCreateZoom}
                rules={[{ required: true, message: 'Please select how to set up the online meeting' }]}
              >
                <Select options={virtualMeetingTypeOptions} />
              </FormItem>,
            );

            if (virtualMeetingType === VirtualMeetingTypeEnum.OwnLink) {
              results.push(
                <FormItem
                  key="eventUrlInput"
                  name="eventUrl"
                  label="Event Online URL"
                  theme="primary"
                  rules={[
                    { required: true, message: 'Please enter your meeting link' },
                    { type: 'url', message: 'The input is not valid web URL' },
                  ]}
                >
                  <Input placeholder="https://..." />
                </FormItem>,
              );
              results.push(
                <FormItem key="passcodeInput" name="passcode" label="Meeting Passcode (optional)" theme="primary">
                  <Input placeholder="Enter passcode" />
                </FormItem>,
              );
            }

            if (virtualMeetingType === VirtualMeetingTypeEnum.AutoCreateGoogleMeet) {
              results.push(
                <GoogleMeetNotice key="googleMeetNotice">
                  You will be asked to sign in to Google when you save. The meeting is created on your own Google
                  Calendar, so allow the popup and grant calendar access to finish setting up the event.
                </GoogleMeetNotice>,
              );
            }

            if (virtualMeetingType === VirtualMeetingTypeEnum.AutoCreateZoom) {
              results.push(
                <FormItem
                  key="autoRecording"
                  name="autoRecording"
                  label="Auto-record Zoom meeting"
                  theme="primary"
                  valuePropName="checked"
                  initialValue={false}
                >
                  <Switch />
                </FormItem>,
              );
            }
          }

          if ([EventTypeEnum.Physical, EventTypeEnum.Both].includes(eventType)) {
            results.push(
              <FormItem key="addressInput" name="address" label="Location Address" theme="primary">
                <SearchBox />
              </FormItem>,
            );
          }

          return results;
        }}
      </Form.Item>
    </>
  );
}

export default EventDetailsStep;

const GoogleMeetNotice = styled.p`
  margin: -8px 0 20px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${colors.primary600};
  background: rgba(244, 185, 51, 0.12);
  font-size: 0.8125rem;
  line-height: 1.45;
  color: ${colors.primary800};
`;
